import React, {useContext, useEffect, useRef, useState} from 'react';
import Card from "../../../components/card";
import TemplateDefault from "../../../components/template-default";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import SelectComponent from "../../../components/select-component";
import {useQuery} from "@tanstack/react-query";
import InputDateTime from "../../../components/input-datetime";
import {statusList} from "../resources/statusList";
import softwareAttendanceService from "../../../services/software-attendance-service";
import InputCheckbox from "../../../components/input-checkbox";
import moment from "moment";
import AlertCard from "../../../components/alert-card";
import InputText from "../../../components/input-text";
import LastAttendances from "../components/last-attendances";
import {ProjectContext} from "../../../context/project-context";
import Button from "../../../components/button";
import {FaExternalLinkAlt, FaStar, FaRegStar} from "react-icons/fa";
import useFetchData from "../hooks/use-fetch-data";
import {toast} from "react-toastify";
import {AlertMessage} from "../../../utils/alert-message";
import MySwal from "sweetalert2";
import ModalRequest from "../components/modal-request";
import general from "../../../utils/general";
import SshService from "../../../services/ssh-service";
import SshModal from "../../../components/ssh-modal";
import requestService from "../../../services/request-service";
import InputForm from "../../../components/input-form";
import FormField from "../../../components/react-hook-form/form-field";
import inputText from "../../../components/input-text";
import TextCorrectorComponent from "../../../components/text-corrector-component";
import {FormProvider, useForm} from "react-hook-form";
import InputRadio from "../../../components/input-radio";

const BASENAME = process.env.PUBLIC_URL;

let initialData = {
    responsible: "",
    dt_initial: "",
    dt_final: "",
    status: {value: 'ABERTO', label: 'Pendente suporte'},
    project: "",
    requester: "",
    module: [],
    type: "",
    template: "",
    problem: "",
    observation: "",
    onsite_visit: "",
    title: "",
    favorite: false,
    internal_notes: "",
    gitlab: "",
    participants: []
}

const SoftwareAttendanceForm = () => {
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const duplicateId = searchParams.get("duplicate-id");

    const navigate = useNavigate();
    const {user} = useContext(ProjectContext);
    const [state, setState] = useState(initialData);
    const [alertMessage, setAlertMessage] = useState("");
    const [resaleOnly, setResaleOnly] = useState(false);
    const [info, setInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [sshModal, setSshModal] = useState(false);
    const [requests, setRequests] = useState([])
    const [request, setRequest] = useState(null)
    const [hasRequestPermission, setHasRequestPermission] = useState(false)
    const [solicitationGroups, setsolicitationGroups] = useState([])
    const [starClass, setStarClass] = useState("")
    const [cantAnswer, setCantAnswer] = useState(null)

    const handleSelect = (value, id) => setState(prev => ({...prev, [id]: value ? value : {value: 'ABERTO', label: 'Multiway'}}));
    const handleInput = ({target: {value, name}}) => setState(prev => ({...prev, [name]: value}));
    const handleDateInput = (value, name) => setState(prev => ({...prev, [name]: value}));
    const handleType = (value) => setState(prev => ({...prev, type: prev.type === value ? "" : value}));

    const attachesRef = useRef();

    useEffect(() => {
        initialData.responsible = {value: user.id, label: user.name};

        if (state.project) {
            document.title = `SI-${state.project.label}#${id ?? ""}`;
        } else {
            document.title = "SISTEMA INTERNO";
        }

        if ((state.status.value.toUpperCase() === "ENCERRADO" || state.status.value.toUpperCase() === "SOLUCIONADO") && !state.dt_final) {
            setState({...state, dt_final: new Date()});
        }
    }, [id, state, user.id, user.name]);

    useEffect(() => {
        if(!id) setState(prev => ({...prev, dt_initial: new Date()}))
    }, [id])

    const {isError: isErrorDuplicate} = useQuery({
        queryFn: () => softwareAttendanceService.getById(duplicateId).then(({data}) => {
            setState({
                ...initialData,
                type: data.type,
                module: data.module,
                problem: data.problem ?? "",
                observation: data.observation ?? "",
                responsible: data?.responsible_selected ?? null,
                participants: data.participants ?? [],
            });

            return data;
        }),
        queryKey: ['hardware-attendance', {duplicateId}],
        enabled: !!duplicateId,
        retry: 0
    });
    if (isErrorDuplicate) navigate("/404");

    const {isError, isFetching, refetch} = useQuery({
        queryFn: () => softwareAttendanceService.getById(id).then(({data}) => {
            const {created_at_formated, user_creation_login, updated_at_formated, user_update_login, request_permissions} = data
            const title = `Cadastrado em ${created_at_formated} por ${user_creation_login} - Última atualização em ${updated_at_formated} por ${user_update_login}`;

            setState({
                dt_initial: data.dt_initial ? new Date(data.dt_initial) : new Date(),
                dt_final: data.dt_final ? new Date(data.dt_final) : "",
                status: statusList.find(status => status.value.toUpperCase() === data.status.toUpperCase()),
                project: data?.project_selected ?? null,
                requester: data?.requester ?? null,
                responsible: data?.responsible_selected ?? null,
                module: data.module,
                type: data.type,
                problem: data.problem ?? "",
                observation: data.observation ?? "",
                onsite_visit: data.onsite_visit ?? "",
                title,
                favorite: data.favorite,
                internal_notes: data.internal_notes ?? "",
                gitlab: data.gitlab ?? "",
                participants: data.participants ?? []
            });

            setInfo(data.info);
            if (data?.project_selected) {
                handleProjectAlerts({
                    ...data.project_selected
                });
            } else {
                setResaleOnly(!!data.resale_only);
            }
            setRequests(data.requests_id ?? [])
            setHasRequestPermission(request_permissions);

            return data;
        }),
        queryKey: ['hardware-attendance', {id}],
        enabled: !!id,
        retry: 0
    });
    if (isError) navigate("/404");

    const {responsibles, projects, modules, templates, requesters} = useFetchData(state.project, id);

    const selectProject = (project) => {
        setState({
            ...state,
            requester: null,
            project
        });

        if (project) {
            handleProjectAlerts(project);
        } else {
            setAlertMessage("")
            setInfo("")
            setResaleOnly(false)
        }
    }

    const handleProjectAlerts = ({expiration_license, expiration_support, resale_only}) => {
        const now = moment(new Date());
        const durationSupport = moment.duration(now.diff(expiration_support));
        const durationLicense = moment.duration(now.diff(expiration_license));
        const daysSupport = Math.round(durationSupport.asDays() * (-1));
        const daysLicense = Math.round(durationLicense.asDays() * (-1));

        let message = "";
        let cantAnswerAttendance = false;
        if (daysSupport <= 0) {
            message = 'Prazo de atendimento expirado para este projeto.';
            cantAnswerAttendance = true;
        } else if (daysLicense <= 15 && daysLicense > 0) {
            message = `Licença vence em ${daysLicense} ${daysLicense === 1 ? "dia" : "dias"}.`;
        } else if (daysLicense <= 0) {
            message = "Licença vencida.";
            cantAnswerAttendance = true;
        }

        setCantAnswer(cantAnswerAttendance)
        setAlertMessage(message);
        setResaleOnly(!!resale_only)
    }

    useEffect(() => {
        if (!alertMessage && !resaleOnly) return;

        MySwal.fire({
            icon: "warning",
            title: "Aviso!",
            confirmButtonColor: "#1D4ED8",
            html: [
                cantAnswer ? `<p>Não podemos atender.</p>` : "",
                alertMessage ? `<p>${alertMessage}</p>` : "",
                resaleOnly ? "<p>Esse projeto deve ser atendido apenas pela revenda vinculada.</p>" : "",
            ].join(""),
        });
    }, [alertMessage, resaleOnly, cantAnswer]);

    const handleRequester = (requester) => {
        if (requester) {
            setInfo({
                user_phone: requester.phone,
                project_phone: state.project.phone,
                user_email: requester.email
            });
        } else {
            setInfo(null);
        }

        setState({...state, requester});
    }

    const openSsh = () => {
        if (!state.project?.value) {
            toast.error(AlertMessage.invalidSsh);
            return;
        }

        if (state.project.ssh.length > 1) {
            setSshModal(true);
            return;
        }

        const ssh = state.project.ssh;
        SshService.openSshConnection(state.project.value, ssh[0]?.id);
    }

    const selectTemplate = (value) => {
        if (value) {
            const {description, items} = value;
            const itemsPrepared = items.map(i => `- ${i.item_description}`).join('\n');

            setState({
                ...state, problem: description, observation: itemsPrepared, template: value
            });
            methods.setValue("problem", description);
            methods.setValue("observation", description);
        }
    }

    const deleteAttendance = () => {
        MySwal.fire({
            icon: "warning",
            title: "Excluir!",
            showCancelButton: true,
            text: "Tem certeza que deseja excluir atendimento?",
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: '#e64942',
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                setIsLoading(true);

                softwareAttendanceService.delete(id)
                    .then(() => {
                        navigate(`/software-attendances`);
                    })
                    .catch((err) => {
                        const {data: {message}} = err.response;
                        toast.error(AlertMessage[message] ?? "Não foi possível excluir");
                    })
                    .finally(() => setIsLoading(false));
            }
        });
    }

    const saveWithRequest = (request) => {
        request.deadline = request.deadline ? general.formatDate(request.deadline) : null;
        submit(request);
    }

    const submit = (saveRequest = null) => {
        setIsLoading(true);

        const dataRaw = methods.getValues();
        const formData = new FormData();

        const form = {
            dt_initial: state.dt_initial ? state.dt_initial.toISOString() : "",
            dt_final: state.dt_final ? state.dt_final.toISOString() : "",
            status: state.status?.value,
            project_id: state.project?.value,
            product_id: state.product?.value,
            requester_id: state.requester?.value ?? "",
            requester: state.requester?.label ?? "",
            responsible_id: state.responsible?.value,
            module_name: JSON.stringify(state.module.map(m => m.label)),
            type: state.type,
            problem: dataRaw?.problem,
            observation: dataRaw?.observation,
            onsite_visit: state.onsite_visit,
            request: saveRequest ? JSON.stringify(saveRequest) : "",
            favorite: state.favorite,
            internal_notes: state.internal_notes ?? "",
            gitlab: state.gitlab ?? "",
            participants: JSON.stringify(
                Array.isArray(state.participants)
                    ? state.participants.map(({value}) => value)
                    : []
            )
        }

        const attachesRow = [];
        attachesRef.current?.api.forEachNode((node) => {
            attachesRow.push(node.data);

            if (node.data?.file) formData.append("attaches[]", node.data.file);
        });

        Object.keys(form).forEach(key => {
            formData.append(key, form[key])
        });

        formData.append("attachs", JSON.stringify(attachesRow));

        if (!!id) formData.append('_method', 'PUT');

        const response = !!id ? softwareAttendanceService.edit(formData, id) : softwareAttendanceService.create(formData);

        response
            .then(({data}) => {
                toast.success("Salvo com sucesso.");
                setOpenModal(false);
                id ? refetch() : navigate(`/software-attendances/form/${data.id}`);
            })
            .catch((err) => {
                const {data: {message}} = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            })
            .finally(() => setIsLoading(false));
    }

    const duplicate = () => {
        window.open(`${BASENAME}/software-attendances/form?duplicate-id=${id}`, `duplicate-${id}`);
    }

    const editRequest = (requestId) => {
        requestService.getById(requestId)
            .then(data => {
                setRequest(data);
                setOpenModal(true)
            })
    }
    const modalGroups = () =>{
        if (state.module){
            setsolicitationGroups({
                groups: state.module
            })
        }
    }

    const handleFavorite = () => {
        setState(prev => ({
            ...prev,
            favorite: !state.favorite
        }))
    }

    useEffect(() => {
        if (state.favorite){
            setStarClass("favorite-class-true")
        }else{
            setStarClass("favorite-class-false")
        }
    }, [state.favorite]);

    const methods = useForm({
        values: state
    });

    return (
        <TemplateDefault title={"Cadastro de atendimento" + (id ? ` #${id}` : "")} isLoading={isLoading || isFetching}>
            <Card title={state.title} headerClass="text-center" titleClass={"text-sm"}>
                <div className="lg:grid xl:grid-cols-3 gap-2">
                    <div className="grid lg:grid-cols-12 gap-4 col-span-2">
                        <div className="col-span-12 lg:col-span-4 text-gray-900">
                            <SelectComponent
                                instanceId="responsible"
                                label="Responsável"
                                options={responsibles ?? []}
                                value={state.responsible}
                                onChange={(value) => handleSelect(value, "responsible")}
                                name="responsible"
                            />
                        </div>
                        <div className="col-span-12 lg:col-span-4 text-gray-900">
                            <SelectComponent
                                isMulti
                                instanceId="participants"
                                label="Participantes"
                                options={responsibles ?? []}
                                value={state.participants}
                                onChange={(value) => handleSelect(value, "participants")}
                                name="participants"
                            />
                        </div>
                        <div className="col-span-4">
                            <div className={"cursor-pointer " + starClass}>
                                {
                                    state.favorite ?
                                        <FaStar onClick={handleFavorite}/>
                                        :
                                        <FaRegStar onClick={handleFavorite}/>
                                }
                            </div>
                        </div>

                        <div className={"col-span-12 lg:col-span-4"}>
                            <InputDateTime id={"date-init"} name={"dt_initial"} label={"Abertura"}
                                           selected={state.dt_initial}
                                           onChange={(value) => handleDateInput(value, "dt_initial")}
                                           showTime/>
                        </div>
                        <div className="col-span-12 lg:col-span-4 text-gray-900">
                            <SelectComponent
                                instanceId="status"
                                label="Status"
                                options={statusList}
                                value={state.status}
                                onChange={(value) => handleSelect(value, "status")}
                            />
                        </div>

                        {
                            (state.status?.value === "ENCERRADO" || state.status?.value === "SOLUCIONADO") &&
                            <div className="col-span-12 lg:col-span-4">
                                <InputDateTime
                                    id={"date-final"} name={"dt_final"} label={"Encerramento"}
                                    selected={state.dt_final}
                                    onChange={(value) => handleDateInput(value, "dt_final")}
                                    showTime
                                />
                            </div>
                        }

                        <div className="col-span-12 lg:col-span-6 text-gray-900">
                            <SelectComponent
                                instanceId="projects"
                                label="Projeto"
                                options={projects ?? []}
                                value={state.project}
                                onChange={selectProject}
                                name="projects"
                            />
                        </div>
                        {
                            user.permissions.includes("dashboard") &&
                            <Button target={"primary"} disabled={!state.project?.value} col={"col-span-2"}
                                    title={"Iniciar conexão SSH"}
                                    onClick={openSsh}>
                                <FaExternalLinkAlt/>
                            </Button>
                        }
                        <div className="col-span-10 lg:col-span-4 text-gray-900">
                            <SelectComponent
                                instanceId="requester"
                                label="Solicitante"
                                options={requesters}
                                value={state.requester}
                                onChange={(value) => handleRequester(value)}
                                formatCreateLabel={(inputValue) => `Criar: ${inputValue}`}
                                name="requester"
                            />
                        </div>

                        <InputForm value={state.gitlab} onChange={handleInput} id="gitlab" name="gitlab" label="GitLab"
                                   col="lg:col-span-12"/>
                        <div className="col-span-12 text-gray-900">
                            <SelectComponent
                                isMulti
                                instanceId="module"
                                label="Grupo"
                                options={modules ?? []}
                                value={state.module}
                                onChange={(value) => handleSelect(value, "module")}
                                name="module"
                            />
                        </div>

                        <div className={"col-span-12 lg:col-span-6 flex lg:flex-row flex-col gap-2 lg:items-center"}>
                            <InputRadio
                                id={"update"}
                                name={"type"}
                                label={"Atualização"}
                                checked={state.type === "Atualização"}
                                onChange={() => {}}
                                onClick={() => handleType("Atualização")}
                            />
                            <InputRadio
                                id={"demo"}
                                name={"type"}
                                label={"Demonstração"}
                                checked={state.type === "Demonstracao"}
                                onChange={() => {}}
                                onClick={() => handleType("Demonstracao")}
                            />
                            <InputCheckbox id={"onsite_visit"} name={"onsite_visit"} label={"Visita in loco"}
                                           onChange={({target: {checked}}) => setState(prev => ({
                                               ...prev,
                                               onsite_visit: checked
                                           }))}
                                           checked={state.onsite_visit}/>
                        </div>
                        <div className="col-span-12 lg:col-span-6 text-gray-900">
                            <SelectComponent
                                instanceId="template"
                                label="Template"
                                options={templates}
                                value={state.template}
                                onChange={selectTemplate}
                                name="template"
                            />
                        </div>
                    </div>

                    <div className="lg:row-span-3 mt-4 lg:mt-0 flex flex-col gap-4 col-span-1">
                        {
                            (!!alertMessage || resaleOnly) &&
                            <AlertCard title="Aviso" type={"danger"}>
                                {cantAnswer && <p>Não podemos atender.</p>}
                                {alertMessage && <p>{alertMessage}</p>}
                                {resaleOnly && <p>Esse projeto deve ser atendido apenas pela revenda vinculada.</p>}
                            </AlertCard>
                        }

                        {
                            state.requester &&
                            <AlertCard title="Informações" type={"info"}>
                                <p>
                                    <b>Solicitante:</b> {info?.user_phone ? info.user_phone.replace(/^(\d{2})(\d{4,5})(\d{4})$/, "($1) $2-$3") : ""}
                                </p>
                                <p>
                                    <b>Projeto:</b> {info?.project_phone ? info.project_phone.replace(/^(\d{2})(\d{4,5})(\d{4})$/, "($1) $2-$3") : ""}
                                </p>
                                <p><b>E-mail:</b> {info?.user_email ?? ""}</p>
                            </AlertCard>
                        }
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-12 mt-4">
                    <FormProvider {...methods}>
                        <div className={"col-span-6 space-y-4"}>
                            <TextCorrectorComponent setIsLoading={setIsLoading} setState={setState} name={"problem"} module={"software_attendances"} url={"/api/si/support/text-correction"}>
                                <FormField
                                    as={inputText}
                                    name={"problem"}
                                    label={"Descrição do Atendimento"}
                                    textAreaClass={"h-40"}
                                    onChange={(e) => {
                                        const valor = e?.target?.value !== undefined ? e.target.value : e;

                                        setState(prev => ({ ...prev, problem: valor }));
                                    }}
                                />
                            </TextCorrectorComponent>
                        </div>
                        <div className={"col-span-6 space-y-4"}>
                            <TextCorrectorComponent setIsLoading={setIsLoading} setState={setState} name={"observation"} module={"software_attendances"} url={"/api/si/support/text-correction"}>
                                <FormField
                                    as={inputText}
                                    name={"observation"}
                                    label={"Resolução do Atendimento"}
                                    textAreaClass={"h-40"}
                                    onChange={(e) => {
                                        const valor = e?.target?.value !== undefined ? e.target.value : e;

                                        setState(prev => ({ ...prev, observation: valor }));
                                    }}
                                />
                            </TextCorrectorComponent>
                        </div>
                    </FormProvider>

                    <InputText name="internal_notes" value={state.internal_notes} onChange={handleInput} label="Anotações internas"
                               col="col-span-12"/>
                </div>

                <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-12 mt-4 items-center">
                {
                    id &&
                    <>
                        <Button onClick={() => {setRequest(null); setOpenModal(true); modalGroups();}} target="primary" col={"col-span-2"}>
                            Gerar solicitação
                        </Button>
                        <div className={"col-span-10 flex items-center"}>
                            {
                                requests.map((r, index) => {
                                    const match = r.match(/^(\d+)\(/);
                                    const number = match ? match[1] : null;
                                    return <p key={index} className={"text-lg hover:text-blue-700 hover:underline cursor-pointer mr-2"}
                                              onClick={() => editRequest(number)}>#{r}</p>
                                })
                            }

                        </div>
                    </>
                }
                </div>
            </Card>

            <LastAttendances project={state.project} setIsLoading={setIsLoading}/>

            <Card wrapperClass={"mt-4"}>
                <div className="flex justify-end flex-wrap gap-3">
                    {
                        id &&
                        <Button onClick={duplicate} target="primary">
                            Duplicar
                        </Button>
                    }
                    {
                        id &&
                        <Button onClick={deleteAttendance} target="danger">
                            Excluir
                        </Button>
                    }
                    <Button onClick={() => navigate("/software-attendances")} target="warning">
                        Cancelar
                    </Button>
                    <Button onClick={() => submit()} target="success">
                        Salvar
                    </Button>
                </div>
            </Card>

            {
                openModal &&
                <ModalRequest
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                    attendance={state}
                    save={saveWithRequest}
                    request={request}
                    hasRequestPermission={hasRequestPermission}
                    createGroups={solicitationGroups}
                    attachesRef={attachesRef}
                    setIsLoading={setIsLoading}
                    correctorModule={"software_attendances"}
                    correctorUrl={"/api/si/support/text-correction"}
                />
            }

            <SshModal open={sshModal} set={setSshModal} project={state.project}/>
        </TemplateDefault>
    );
};

export default SoftwareAttendanceForm;
